import React, { useState, useRef, useCallback } from 'react';
import { X, RotateCw, ZoomIn, ZoomOut, Download, Eye } from 'lucide-react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ImageCropperModal = ({ isModalOpen, setIsModalOpen, image,
  setImage,
  crop,
  setCrop,
  zoom,
  setZoom,
  rotation,
  setRotation,
  completedCrop,
  setCompletedCrop,
  showPreview,
  setShowPreview,
  previewImage,
  setPreviewImage, handleCropComplete, imageLoading }) => {

  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const fileInputRef = useRef(null);


  const getRotatedSize = (width, height, rotation) => {
    const rad = (rotation * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(rad));
    const absSin = Math.abs(Math.sin(rad));

    return {
      width: Math.floor(width * absCos + height * absSin),
      height: Math.floor(width * absSin + height * absCos)
    };
  };

  const handleZoomSlider = (e) => {
    const value = parseFloat(e.target.value);
    setZoom(value);
  };

  const handleRotateSlider = (e) => {
    const value = parseInt(e.target.value, 10);
    setRotation(value % 360); // Keep rotation within 0-359 degrees
  };

  // const onLoad = useCallback((img) => {
  //   const imageElement = img.target;
  //   imgRef.current = imageElement;
  //   setImageDimensions({
  //     width: imageElement.naturalWidth,
  //     height: imageElement.naturalHeight
  //   });
  // }, []);

  const onLoad = useCallback((img) => {
    const imageElement = img.target;
    imgRef.current = imageElement;

    setImageDimensions({
      width: imageElement.naturalWidth,
      height: imageElement.naturalHeight
    });

    // ✅ Set default crop if not set
    if (!completedCrop?.width || !completedCrop?.height) {
      const initialCrop = {
        unit: 'px',
        x: 50,
        y: 50,
        width: Math.min(200, imageElement.naturalWidth),
        height: Math.min(200, imageElement.naturalHeight)
      };
      setCrop(initialCrop);
      setCompletedCrop(initialCrop);
    }
  }, [completedCrop, setCrop, setCompletedCrop]);


  const generatePreview = useCallback(async () => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio;

    // Get rotated dimensions
    const rotatedSize = getRotatedSize(crop.width, crop.height, rotation);
    canvas.width = rotatedSize.width * pixelRatio;
    canvas.height = rotatedSize.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    // Center the canvas
    ctx.translate(canvas.width / pixelRatio / 2, canvas.height / pixelRatio / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);

    // Draw the image centered
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      -crop.width / 2,
      -crop.height / 2,
      crop.width,
      crop.height
    );

    const dataURL = canvas.toDataURL();
    setPreviewImage(dataURL);
  }, [completedCrop, zoom, rotation]);

  // Update preview when crop changes
  React.useEffect(() => {
    if (showPreview && completedCrop) {
      generatePreview();
    }
  }, [completedCrop, generatePreview, showPreview]);

  const cropImage = async () => {
    if (!completedCrop || !imgRef.current) return;

    const image = imgRef.current;
    const crop = completedCrop;

    if (!image.complete) {
      await new Promise((resolve) => {
        image.addEventListener('load', resolve);
      });
    }

    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio;

    // Get rotated dimensions
    const rotatedSize = getRotatedSize(crop.width, crop.height, rotation);
    canvas.width = rotatedSize.width * pixelRatio;
    canvas.height = rotatedSize.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    // Center the canvas
    ctx.translate(canvas.width / pixelRatio / 2, canvas.height / pixelRatio / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);

    // Draw the image centered
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      -crop.width / 2,
      -crop.height / 2,
      crop.width,
      crop.height
    );

    // canvas.toBlob((blob) => {
    //   const url = URL.createObjectURL(blob);
    //   const a = document.createElement('a');
    //   a.href = url;
    //   a.download = 'cropped-image.png';
    //   a.click();
    //   URL.revokeObjectURL(url);
    // });
  };

  const resetCrop = () => {
    setCrop({
      unit: 'px',
      x: 50,
      y: 50,
      width: 200,
      height: 200
    });
    setZoom(1);
    setRotation(0);
  }

  return (
    <>
      {isModalOpen && <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          maxWidth: '1152px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937',
              margin: 0
            }}>Image Cropper</h2>
            <button
              onClick={() => setIsModalOpen(false)}
              style={{
                padding: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <X size={20} />
            </button>
          </div>

          {/* Controls */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                gap: '24px',
                alignItems: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <ZoomOut size={16} />
                  <input
                    type="range"
                    min={0.5}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={handleZoomSlider}
                    style={{ width: '128px' }}
                  />
                  <ZoomIn size={16} />
                  <span style={{
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    backgroundColor: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #d1d5db',
                    minWidth: '60px',
                    textAlign: 'center'
                  }}>
                    {Math.round(zoom * 100)}%
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <RotateCw size={16} />
                  <input
                    type="range"
                    min={0}
                    max={359}
                    step={1}
                    value={rotation}
                    onChange={handleRotateSlider}
                    style={{ width: '128px' }}
                  />
                  <span style={{
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    backgroundColor: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #d1d5db',
                    minWidth: '60px',
                    textAlign: 'center'
                  }}>
                    {rotation}°
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={resetCrop}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#e5e7eb',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d1d5db'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                >
                  Reset
                </button>
                <button
                  onClick={() => handleCropComplete(previewImage)}
                  disabled={previewImage == null}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: previewImage == null ? "#dddd" : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'

                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                >
                  {/* <Download size={16} /> */}
                  {imageLoading ? "Cropping..." : "Crop "}
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div style={{
            flex: 1,
            display: 'flex',
            minHeight: 0
          }}>
            {/* Image Editor */}
            <div style={{
              flex: 1,
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {image && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  style={{
                    maxHeight: 'calc(90vh - 200px)',
                    maxWidth: '100%'
                  }}
                >
                  <img
                    ref={imgRef}
                    src={image}
                    alt="Crop preview"
                    style={{
                      transform: `scale(${zoom})`,
                      maxHeight: 'calc(90vh - 200px)',
                      maxWidth: '100%'
                    }}
                    onLoad={onLoad}
                  />
                </ReactCrop>
              )}
            </div>

            {/* Preview Panel */}
            {showPreview && (
              <div style={{
                width: '320px',
                padding: '16px',
                borderLeft: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  margin: '0 0 16px 0'
                }}>Preview</h3>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb'
                }}>
                  {previewImage ? (
                    <img
                      src={previewImage}
                      alt="Crop preview"
                      style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db'
                      }}
                    />
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '160px',
                      color: '#9ca3af'
                    }}>
                      <Eye size={48} style={{ opacity: 0.3, marginBottom: '8px' }} />
                      <p style={{ textAlign: 'center', margin: 0 }}>Preview will appear here</p>
                    </div>
                  )}
                </div>
                {previewImage && (
                  <div style={{
                    marginTop: '16px',
                    fontSize: '14px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}>
                      <span>Size:</span>
                      <span style={{ fontFamily: 'monospace' }}>{completedCrop?.width} × {completedCrop?.height}px</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}>
                      <span>Rotation:</span>
                      <span style={{ fontFamily: 'monospace' }}>{rotation}°</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>Zoom:</span>
                      <span style={{ fontFamily: 'monospace' }}>{Math.round(zoom * 100)}%</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Info Panel */}
          <div style={{
            padding: '16px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                gap: '24px',
                fontSize: '14px'
              }}>
                <span>Crop: <span style={{ fontFamily: 'monospace' }}>{completedCrop?.width || 0} × {completedCrop?.height || 0}px</span></span>
                <span>Position: <span style={{ fontFamily: 'monospace' }}>({Math.round(completedCrop?.x || 0)}, {Math.round(completedCrop?.y || 0)})</span></span>
                <span>Zoom: <span style={{ fontFamily: 'monospace' }}>{Math.round(zoom * 100)}%</span></span>
                <span>Rotation: <span style={{ fontFamily: 'monospace' }}>{rotation}°</span></span>
              </div>
              <div style={{
                fontSize: '14px',
                color: '#6b7280'
              }}>
                Drag to move crop area • Drag handles to resize
              </div>
            </div>
          </div>

          {/* Hidden canvas for preview */}
          <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
        </div>
      </div>}
    </>
  );
};

export default ImageCropperModal;